import { DispatchCard } from 'src/entities/dispatch';
import { RotationRow } from 'src/pages/ConvenienceDispatches/adapter';

import { hasAssignedDelivery } from './vehicleRowExpand/assignedDelivery';

/**
 * その車両に配車が 1 件も無いか（=「計画無」）。
 *
 * 回転ごとではなく車両ごとに見る。バッジの × は勤務（Shift）ごと消す操作なので、
 * 「2 回転目だけ空」で × を出すと、計画のある 1 回転目まで消えてしまう。
 */
export function isNoPlanVehicle(rows: readonly RotationRow[]): boolean {
  return !rows.some((row) => hasAssignedDelivery(row.cards));
}

/**
 * 配車が 1 件も無い勤務 (shift_truck) の集合。
 *
 * 車両単位で見ないのは、1 台に複数のドライバーが同じ時間帯で乗る勤務があるため。
 * 誰か 1 人でも配車されていれば「計画あり」になり、配車の無いドライバーの行に
 * 「計画無」が出なくなる。
 *
 * 同じ勤務の中は回転をまたいで見る（2 回転目だけ空でも計画無にはしない）。
 * 勤務を持たない行（回転追加の仮想行）は実体が無いので judge しない。
 */
/**
 * 「計画無」バッジを出す行の rowId。
 *
 * バッジと × は勤務ごとに 1 つ。同じ勤務の全回転に並べると「回転単位の状態」と誤読され、
 * × も同じ勤務に対して何度も並ぶ。
 */
export function buildNoPlanBadgeRowIds(rows: readonly RotationRow[]): Set<string> {
  const noPlanShiftTruckIds = buildNoPlanShiftTruckIds(rows);
  const seen = new Set<number>();
  const rowIds = new Set<string>();
  rows.forEach((row) => {
    if (row.shiftTruckId == null) return;
    if (!noPlanShiftTruckIds.has(row.shiftTruckId) || seen.has(row.shiftTruckId)) return;
    seen.add(row.shiftTruckId);
    rowIds.add(row.rowId);
  });
  return rowIds;
}

export function buildNoPlanShiftTruckIds(
  rows: readonly RotationRow[],
  /** 「配車がある」の見方。画面によって未反映の D&D を数えるかが違う */
  hasPlanCards: (cards: readonly DispatchCard[]) => boolean = hasAssignedDelivery,
): Set<number> {
  const assignedByShiftTruck = new Map<number, boolean>();
  rows.forEach((row) => {
    if (row.shiftTruckId == null) return;
    const already = assignedByShiftTruck.get(row.shiftTruckId) ?? false;
    assignedByShiftTruck.set(row.shiftTruckId, already || hasPlanCards(row.cards));
  });
  const noPlan = new Set<number>();
  assignedByShiftTruck.forEach((assigned, shiftTruckId) => {
    if (!assigned) noPlan.add(shiftTruckId);
  });
  return noPlan;
}
